import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, Award, Users, Sparkles, Target, Heart } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="about-page">
      <Navbar />
      <div className="grain-overlay" />

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="max-w-5xl mx-auto px-4 md:px-8 pt-16 pb-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-[0.1em] mb-4">
              About <span className="text-primary">SŌHM</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Reimagining precious metal ownership for the next generation. 
              Where tradition meets modern collecting culture.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 space-y-16">
        {/* Our Story */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl font-bold uppercase tracking-wide mb-6 flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-primary" />
            Our Story
          </h2>
          <div className="bg-card border border-border/40 rounded-sm p-6 md:p-8">
            <p className="text-muted-foreground leading-relaxed mb-4">
              SŌHM was born from a simple observation: while gold and silver have been treasured for millennia, 
              the way young people invest in them hasn't evolved. We saw sneaker drops, limited-edition collectibles, 
              and NFTs capturing the imagination of Gen-Z, and wondered — why can't precious metals be just as exciting?
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Founded in 2026, we set out to create a platform that combines the timeless value of physical gold 
              and silver with the thrill of modern drop culture. Each piece in our collection isn't just an investment — 
              it's a work of art, inspired by Indian heritage and contemporary design.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              See each product listing for its stated purity, hallmark information and any available certificate 
              of authenticity. We believe in complete transparency — from metal purity to pricing breakdown.
            </p>
          </div>
        </motion.section>

        {/* Values */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h2 className="font-display text-2xl font-bold uppercase tracking-wide mb-6 flex items-center gap-3">
            <Heart className="w-6 h-6 text-primary" />
            What We Stand For
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Shield, title: 'Trust & Transparency', desc: 'Purity and hallmark details are shown per product. Every price is broken down. No hidden fees, ever.' },
              { icon: Award, title: 'Quality Craftsmanship', desc: 'We partner with master artisans to create collectibles that are both beautiful and valuable.' },
              { icon: Users, title: 'Community First', desc: 'We\'re building more than a marketplace — we\'re building a community of modern collectors.' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="bg-card border border-border/40 rounded-sm p-5"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <item.icon className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-display font-bold uppercase text-sm mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Mission */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="font-display text-2xl font-bold uppercase tracking-wide mb-6 flex items-center gap-3">
            <Target className="w-6 h-6 text-primary" />
            Our Mission
          </h2>
          <div className="bg-primary/5 border border-primary/20 rounded-sm p-6 md:p-8 text-center">
            <p className="text-lg md:text-xl text-foreground/90 font-medium italic">
              "To make precious metal ownership accessible, exciting, and transparent for the next generation of investors."
            </p>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section
          className="text-center py-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="font-display text-xl font-bold uppercase mb-4">Ready to Start Your Collection?</h3>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/">
              <Button className="bg-primary text-primary-foreground px-6">
                Explore Drops
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white/20">
                Contact Us
              </Button>
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
